import React, { useRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { Form, Input, Radio } from '~/shared/components/form';
import { Grid } from '~/shared/components/Grid/Grid';
import { useTranslation } from '~/shared/utils/translation';
import { useValidationSchema } from './lib/useValidationSchema';
import { Summery } from './components/Summery';
import Spacer from '~/theme/design-system/components/Spacer';
import { Fields, InstoreInformationInputs } from '../../models/fields';
import { useCheckoutState } from '../../hooks/useCheckoutState';
import {
    StyledButton,
    StyledGrid,
    StyledTable,
    StyledTableCell,
    StyledTableCellRightSpace,
    StyledTableRow,
} from './styled';
import { useCityByPostCode } from '~/shared/hooks/useCitySearch/useCityByPostCode';
import { StyledDesktopCart } from '../DeliveryAddressOption/styled';
import {
    StyledCheckoutMain,
    StyledCheckoutWrapper,
} from '~/templates/pages/components/CheckOutLayout/styled';
import { StyledCheckoutAside } from '~/templates/pages/components/CheckOutLayout/styled';
import { useBasket } from '~/features/basket';
import { useInStoreState } from '../../hooks/useInStoreState';
import { useSubmit } from './lib/useSubmit';
import { useLookupCustomer } from '~/services/customer/useLookupCustomer';
import { CouponItem } from '~/lib/data-contract/salesforce';
import { useCreateCustomer } from '~/services/customer/useCreateCustomer';
import { useSuccessInfo } from '~/features/basket/components/notify/toast';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { useLocalBasketState } from '~/features/basket/hooks/useLocalBasketState';
import { Phone } from '~/shared/components/form/components/form-fields/phone';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { AddressSuggestion } from '~/shared/hooks/useAddressAutocomplete/api/models';
import { toAddressLine } from '~/shared/hooks/useAddressAutocomplete/utils';

const FallbackStoreZip = '2760';

export const InStore = () => {
    const { translate } = useTranslation();
    const storePersonal = useCustomer();
    const showSuccess = useSuccessInfo();
    const [canCreateCustomer, setCanCreateCustomer] = useState(false);
    const { displayOnlyError: showError } = useErrorHandler();

    const { createCustomer } = useCreateCustomer();
    const basket = useBasket();
    const { handlers } = useSubmit();
    const { setCustomerInfo, ...customerInfo } = useInStoreState();
    const { membership } = useLocalBasketState();
    const { loopupCustomer } = useLookupCustomer();

    const {
        goForward,
        zipCode: zipCodeInState,
        setBillingAddress,
        setZipCode,
    } = useCheckoutState((store) => ({
        goForward: store.goForward,
        zipCode: store.zipCode,
        setZipCode: store.setZipCode,
        setBillingAddress: store.setBillingAddress,
    }));
    const validationSchema = useValidationSchema();
    const buttonRef = useRef<HTMLButtonElement>(null);

    const methods = useForm<InstoreInformationInputs>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
    });

    const zipCode = methods.watch(Fields.PostalCode);
    const { cityName, hasNoMatchingPostCode, isFetchingCityByPostCode } =
        useCityByPostCode(zipCode);
    // formstate must be valid and if we should create the user it must also be possible
    const isValid = methods.formState.isValid && ((membership && canCreateCustomer) || true);

    useEffect(() => {
        if (!zipCode) {
            methods.setValue(Fields.City, '');
            return;
        }

        if (isFetchingCityByPostCode) {
            methods.setValue(Fields.City, '');
            return;
        }

        if (cityName) {
            methods.setValue(Fields.City, cityName);
            methods.clearErrors(Fields.PostalCode);
            return;
        }

        if (hasNoMatchingPostCode) {
            methods.setError(Fields.PostalCode, {
                message: translate('message.postNumberValidationError.notFound'),
            });
            methods.setValue(Fields.City, '');
            methods.setValue(Fields.PostalCode, '');
        }
    }, [cityName, hasNoMatchingPostCode, isFetchingCityByPostCode, zipCode]);

    useEffect(() => {
        if (storePersonal.isInitialized) {
            if (storePersonal.addresses) {
                const [storeAddress] = storePersonal.addresses;
                setZipCode(storeAddress.postalCode ?? FallbackStoreZip);
            }
        }
    }, [storePersonal.isInitialized, storePersonal.isStorePersonnel]);

    useEffect(() => {
        methods.setValue(Fields.Email, customerInfo.email);
        methods.setValue(Fields.Phone, customerInfo.phoneMobile);
        methods.setValue(Fields.Address1, customerInfo.address1);
        methods.setValue(Fields.PostalCode, customerInfo.postalCode);
        methods.setValue(Fields.City, customerInfo.city);
        methods.setValue(Fields.FirstName, customerInfo.firstName);
        methods.setValue(Fields.LastName, customerInfo.lastName);
        methods.setValue(Fields.StoreZip, storePersonal?.c_storeZip ?? FallbackStoreZip);
        methods.setValue(Fields.Gender, '3');

        return () => {
            autoSave();
        };
    }, []);

    const autoSave = async () => {
        const payload = methods.getValues();
        await setCustomerInfo(payload);
    };

    const registerNewCustomer = async () => {
        const { email, phoneMobile, address1, city, firstName, lastName, postalCode, gender } =
            methods.getValues();

        try {
            const result = await createCustomer({
                email,
                firstname: firstName,
                lastname: lastName,
                mobile: String(phoneMobile),
                address: address1,
                city,
                postalCode,
                gender,
            });
            if (!result) {
                return showError(translate('checkout.instore.customerCouldNotBeCreatedError'));
            }

            if (result.httpStatus == 409) {
                return showError(translate('checkout.instore.customerAlreadyExistsError'));
            }

            if (result.httpStatus == 201) {
                // apply membership code, so the user will recieve bonus on this first order
                const memberCoupon = basket?.couponItems?.find(
                    (item: CouponItem) => item.code === 'instoremember',
                );

                if (!memberCoupon) {
                    basket.applyPromoCode('instoremember');
                }
                return showSuccess(translate('checkout.instore.customerSuccesfullyCreated'));
            }

            return showError(translate('checkout.instore.customerCouldNotBeCreatedError'));
        } catch (error) {
            showError(translate('checkout.instore.customerCouldNotBeCreatedError'));
        }
    };

    const handleSelectAddress = ({ data }: AddressSuggestion) => {
        const { postnr, postnrnavn } = data;

        methods.setValue(Fields.Address1, toAddressLine(data), { shouldValidate: true });
        methods.setValue(Fields.PostalCode, postnr || '');
        methods.setValue(Fields.City, postnrnavn || '');

        if (postnr) {
            methods.clearErrors(Fields.PostalCode);
        }
    };

    const handleSubmit = async (payload: InstoreInformationInputs) => {
        /* Also use this for shipping as default */
        await handlers.submitInformation(payload, true);

        if (!zipCodeInState) {
            setZipCode(payload.postalCode);
        }

        // Only create the new customer if "Become a member" has been checked on the basket page
        if (membership) {
            await registerNewCustomer();
        }

        setBillingAddress(payload);
        setCustomerInfo(payload);

        goForward();
    };

    const getMemberCoupon = () =>
        basket?.couponItems?.find((item: CouponItem) => item.code === 'instoremember');

    const handleLookupCustomer = async (e: React.FocusEvent<HTMLInputElement>) => {
        if (
            !!e.target.value &&
            (!methods.formState.errors[Fields.Email] || !methods.formState.errors[Fields.Phone])
        ) {
            const { email, phoneMobile } = methods.getValues();

            if (email && phoneMobile) return;

            const customer = await loopupCustomer({ email, phone: String(phoneMobile) });

            if (customer && !customer?.error) {
                setCanCreateCustomer(false);
                methods.setValue(Fields.FirstName, customer?.profile?.firstName);
                methods.setValue(Fields.LastName, customer?.profile?.lastName);
                methods.setValue(Fields.Phone, customer?.profile?.phone);
                methods.setValue(Fields.Email, customer?.profile?.email);
                methods.setValue(Fields.Gender, customer?.profile?.gender || '3');

                if (customer?.addressBook?.addresses) {
                    const [address] = customer?.addressBook?.addresses;
                    methods.setValue(Fields.Address1, address?.address1);
                    methods.setValue(Fields.PostalCode, address?.postalCode);
                }
                if (customer?.profile?.custom?.isMemberPlus) {
                    const memberCoupon = basket?.couponItems?.find(
                        (item: CouponItem) => item.code === 'instoremember',
                    );

                    if (!memberCoupon) {
                        basket.applyPromoCode('instoremember');
                    }
                }
            } else {
                setCanCreateCustomer(true);

                const memberCoupon = getMemberCoupon();

                if (memberCoupon?.couponItemId) {
                    basket.removePromoCode(memberCoupon.couponItemId);
                }
            }
        } else {
            setCanCreateCustomer(true);

            methods.setValue(Fields.FirstName, '');
            methods.setValue(Fields.LastName, '');
            methods.setValue(Fields.Phone, '');
            methods.setValue(Fields.Email, '');
            methods.setValue(Fields.Address1, '');
            methods.setValue(Fields.PostalCode, '');
            methods.setValue(Fields.City, '');
            methods.setValue(Fields.Gender, '');

            const memberCoupon = getMemberCoupon();
            if (memberCoupon?.couponItemId) {
                basket.removePromoCode(memberCoupon.couponItemId);
            }
        }

        methods.trigger();
    };

    return (
        <StyledCheckoutWrapper>
            <StyledCheckoutMain>
                <Form methods={methods} onSubmit={handleSubmit}>
                    <Grid flow="row" rowGap="extrasmall">
                        <Input
                            name={Fields.Email}
                            label={translate('forms.fields.form.mail')}
                            onBlur={handleLookupCustomer}
                            maxLength={50}
                            required
                        />

                        <Phone
                            name={Fields.Phone}
                            label={translate('forms.fields.form.mobile')}
                            maxCharacters={8}
                            onBlur={handleLookupCustomer}
                            required
                        />

                        <Spacer space="20px" />

                        <Input type="hidden" name="useAsShipping" value="true"></Input>

                        <StyledGrid flow="column" columns="1fr 1fr" columnGap="extrasmall">
                            <Input
                                name={Fields.FirstName}
                                label={translate('forms.fields.form.firstName')}
                                required
                            />

                            <Input
                                name={Fields.LastName}
                                label={translate('forms.fields.form.lastName')}
                                required
                            />
                        </StyledGrid>

                        <AddressAutocomplete
                            name={Fields.Address1}
                            label={translate('forms.fields.form.address1')}
                            maxLength={50}
                            onSelectAddress={handleSelectAddress}
                        />

                        <StyledGrid flow="column" columns="1fr 2fr" columnGap="extrasmall">
                            <Input
                                name={Fields.PostalCode}
                                label={translate('forms.fields.form.postalcode')}
                                maxLength={4}
                            />

                            <Input
                                name={Fields.City}
                                label={translate('forms.fields.form.city')}
                                disabled
                            />
                        </StyledGrid>
                        {membership && (
                            <StyledTable>
                                <StyledTableRow>
                                    <StyledTableCell>
                                        <Radio type="radio" name={Fields.Gender} value="2" />
                                    </StyledTableCell>
                                    <StyledTableCellRightSpace>
                                        {translate('createAccount.female')}
                                    </StyledTableCellRightSpace>
                                    <StyledTableCell>
                                        <Radio type="radio" name={Fields.Gender} value="1" />
                                    </StyledTableCell>
                                    <StyledTableCellRightSpace>
                                        {translate('createAccount.male')}
                                    </StyledTableCellRightSpace>
                                    <StyledTableCell>
                                        <Radio type="radio" name={Fields.Gender} value="3" />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {translate('createAccount.genderOther')}
                                    </StyledTableCell>
                                </StyledTableRow>
                            </StyledTable>
                        )}

                        <Input type="hidden" name={Fields.StoreZip} defaultValue="2300" />

                        <StyledButton
                            aria-label={translate('checkout.ctaLabel.step1')}
                            type="submit"
                            ref={buttonRef}
                            size="large"
                            disabled={!isValid}
                        >
                            {translate('checkout.ctaLabel.step1')}
                        </StyledButton>
                    </Grid>
                </Form>
            </StyledCheckoutMain>

            <StyledCheckoutAside>
                <StyledDesktopCart>
                    <Summery
                        ctaLabel={translate('checkout.ctaLabel.step1')}
                        onSubmit={() => buttonRef.current?.click()}
                        disabledButton={!isValid}
                    />
                </StyledDesktopCart>
            </StyledCheckoutAside>
        </StyledCheckoutWrapper>
    );
};
